import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, TextField, IconButton, Tooltip, Link, Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import VideocamIcon from '@mui/icons-material/Videocam';
import { pushModal } from '../../actions/modals';
import { ATTACHMENT_MODAL } from '../../actions/types';
import { userStamp } from '../../utils/dateFunctions';
import getMediaType, { isImageFile, isPdfFile, isVideoFile } from '../../utils/mediaTypes';
import { canDeleteAttachment } from './partsUtils';
import AttachmentVideo from '../AttachmentVideo';

const nameOf = attachment => attachment.fileid || attachment.filename || 'file';

const kindOf = (attachment) => {
  const name = nameOf(attachment);
  if (isImageFile(name)) return 'image';
  if (isVideoFile(name)) return 'video';
  if (isPdfFile(name)) return 'pdf';
  return 'other';
};

const AttachmentCaptionRow = ({
  attachment, onSaveCaption, onDelete, onSetAsImage,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.current_user || {});
  const [caption, setCaption] = useState(attachment.caption || '');
  const [imageVisible, setImageVisible] = useState(false);

  const canDelete = canDeleteAttachment(currentUser, attachment);

  const kind = kindOf(attachment);
  const filename = nameOf(attachment);

  const saveCaption = () => {
    if ((caption || '') === (attachment.caption || '')) return;
    onSaveCaption(caption);
  };

  const openPdf = () => dispatch(pushModal({
    type: ATTACHMENT_MODAL,
    props: { title: filename, attachment },
  }));

  const download = async () => {
    const response = await fetch(attachment.location);
    const blob = await response.blob();
    const typed = new Blob([blob], { type: getMediaType(filename) });
    const url = window.URL.createObjectURL(typed);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  return (
    <Box sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
        {kind === 'image' ? (
          <Box
            component="img"
            src={attachment.location}
            alt={filename}
            onClick={() => setImageVisible(prev => !prev)}
            sx={{
              width: 56, height: 56, objectFit: 'cover', borderRadius: 1, cursor: 'pointer', flexShrink: 0,
            }}
          />
        ) : (
          <Box sx={{
            width: 56,
            height: 56,
            borderRadius: 1,
            bgcolor: '#f0f0f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          >
            {kind === 'pdf' && <PictureAsPdfIcon color="error" />}
            {kind === 'video' && <VideocamIcon color="action" />}
            {kind !== 'pdf' && kind !== 'video' && <InsertDriveFileIcon color="disabled" />}
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Link
            href={attachment.location}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            sx={{
 display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
}}
          >
            {filename}
          </Link>
          <TextField
            size="small"
            variant="standard"
            placeholder="Add a caption / note"
            fullWidth
            sx={{ mt: 0.5 }}
            value={caption}
            onChange={e => setCaption(e.target.value)}
            onBlur={saveCaption}
          />
          <Typography variant="caption" color="textSecondary">
            {attachment.user_name ? `Uploaded by ${String(attachment.user_name).split('@')[0]} ` : 'Uploaded '}
            {attachment.created_at ? `on ${userStamp(attachment.created_at)}` : ''}
          </Typography>
        </Box>

        <Box sx={{
 display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0,
}}
        >
          {kind === 'image' && (
            <Button size="small" startIcon={<ImageIcon />} onClick={() => setImageVisible(prev => !prev)}>
              {imageVisible ? 'Hide' : 'View'}
            </Button>
          )}
          {kind === 'image' && onSetAsImage && (
            <Tooltip title="Use this as the part image shown in the catalog and part search">
              <Button size="small" startIcon={<PhotoLibraryIcon />} onClick={onSetAsImage}>
                Set as part image
              </Button>
            </Tooltip>
          )}
          {kind === 'pdf' && (
            <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={openPdf}>
              Preview
            </Button>
          )}
          <Tooltip title="Download">
            <IconButton size="small" color="info" onClick={download}><DownloadIcon fontSize="small" /></IconButton>
          </Tooltip>
          {canDelete && (
            <Tooltip title="Delete file">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  if (!window.confirm('Delete this file?')) return;
                  onDelete();
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {kind === 'image' && imageVisible && (
        <Box sx={{ mt: 1.5, textAlign: 'center' }}>
          <Box
            component="img"
            src={attachment.location}
            alt={filename}
            sx={{ maxWidth: '100%', maxHeight: 500, borderRadius: 1 }}
          />
        </Box>
      )}
      {kind === 'video' && (
        <Box sx={{ mt: 1.5 }}>
          <AttachmentVideo src={attachment.location} filename={filename} />
        </Box>
      )}
    </Box>
  );
};

export default AttachmentCaptionRow;
